import { Component, OnInit, HostListener } from '@angular/core';
import { OtherProductsService, OtherProduct } from '../service/other-products.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

// ── Dropdown product definition ──────────────────────────────────────────────
export interface ProductOption {
  label: string;           // Display name in dropdown
  key: OtherProduct;       // Internal key
  apiType: 'consumer' | 'professional'; // Which API shape to expect
  planFilter?: string;     // plan value to filter on (consumer APIs)
}

// ── Consumer shape (Vitagist) ────────────────────────────────────────────────
export interface Consumer {
  id: string;
  name?: string;                  // Single name field (new API format)
  first_name?: string | null;     // Legacy support
  last_name?: string | null;      // Legacy support
  email: string;
  phone: string | null;
  plan?: string;
  joined?: string | null;
  lastlogin?: string | null;
  account_activation_date?: string;
  last_login?: string;
  status?: string;
}

// ── Professional shape (DGC / Dilite) ───────────────────────────────────────
export interface Professional {
  name: string;
  uid: string;
  contact_person: string | null;
  email: string;
  plan: string;
  contact_phone: string;
  joined: string | null;
  status: string;
}

export type AnyRecord = Consumer | Professional;

// ── Static product list ──────────────────────────────────────────────────────
const PRODUCTS: ProductOption[] = [
  { label: 'Vitagist',       key: 'vitagist',  apiType: 'consumer',      planFilter: 'vitagist' },
  { label: 'Dilite',         key: 'dilite',    apiType: 'professional' },
  { label: 'Content Coffer', key: 'content',   apiType: 'professional' },
  { label: 'Coffer Connect', key: 'connect',   apiType: 'professional' },
  { label: 'RegsWatch',      key: 'regswatch', apiType: 'professional' },
];

@Component({
    selector: 'app-other-products',
    templateUrl: './other-products.component.html',
    styleUrls: ['./other-products.component.css'],
    standalone: false
})
export class OtherProductsComponent implements OnInit {

  // ─── Dropdown ─────────────────────────────────────────────────────────────
  readonly products = PRODUCTS;
  selectedProduct: ProductOption | null = null;
  isDropdownOpen: boolean = false;

  // ─── Data ─────────────────────────────────────────────────────────────────
  allRecords: any[] = [];
  filteredData: any[] = [];
  dataSource: any[] = [];

  // ─── State ────────────────────────────────────────────────────────────────
  isLoading: boolean = false;

  // ─── Search ───────────────────────────────────────────────────────────────
  searchQuery: string = '';

  // ─── Sort ─────────────────────────────────────────────────────────────────
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  // ─── Pagination ───────────────────────────────────────────────────────────
  pageIndex: number = 0;
  pageSize: number = 10;

  constructor(
    private service: OtherProductsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const vitagist = this.products.find(p => p.key === 'vitagist');
    if (vitagist) { this.selectProduct(vitagist); }
  }

  // ─── Close dropdown on outside click ──────────────────────────────────────
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.product-dropdown-wrapper')) {
      this.isDropdownOpen = false;
    }
  }

  // ═══════════════════════════════════════════════════════════
  //  DROPDOWN
  // ═══════════════════════════════════════════════════════════

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  selectProduct(product: ProductOption): void {
    this.selectedProduct = product;
    this.isDropdownOpen = false;
    this.resetState();
    this.loadData(product);
  }

  clearProduct(): void {
    this.selectedProduct = null;
    this.resetState();
  }

  private resetState(): void {
    this.allRecords  = [];
    this.filteredData = [];
    this.dataSource  = [];
    this.searchQuery = '';
    this.sortColumn  = '';
    this.sortDirection = '';
    this.pageIndex   = 0;
  }

  // ═══════════════════════════════════════════════════════════
  //  API
  // ═══════════════════════════════════════════════════════════

  private loadData(product: ProductOption): void {
    this.isLoading = true;
    this.spinner.show();

    let request$: any;

    switch (product.key) {
      case 'vitagist':  request$ = this.service.getVitagistConsumers();      break;
      case 'dilite':    request$ = this.service.getDiliteProfessionals();     break;
      case 'content':   request$ = this.service.getContentProfessionals();   break;
      case 'connect':   request$ = this.service.getConnectProfessionals();   break;
      case 'regswatch': request$ = this.service.getRegswatchProfessionals(); break;
    }

    request$.subscribe(
      (response: any) => {
        if (response && Array.isArray(response.data)) {
          if (product.apiType === 'consumer' && product.planFilter) {
            // Consumer API: filter by plan
            this.allRecords = response.data.filter(
              (item: Consumer) =>
                (item.plan || '').toLowerCase() === product.planFilter!.toLowerCase()
            );
          } else {
            // Professional API: all records returned are already scoped by the endpoint
            this.allRecords = response.data;
          }
        } else {
          this.allRecords = [];
        }
        this.applyFilter();
        this.isLoading = false;
        this.spinner.hide();
      },
      (error: any) => {
        console.error(`Error loading ${product.label}:`, error);
        this.toastr.error(`Failed to load ${product.label} data`, 'Error');
        this.allRecords = [];
        this.dataSource = [];
        this.isLoading = false;
        this.spinner.hide();
      }
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  FILTER & SORT
  // ═══════════════════════════════════════════════════════════

  applyFilter(): void {
    const q = this.searchQuery.trim().toLowerCase();
    let filtered = [...this.allRecords];

    if (q && this.selectedProduct) {
      if (this.selectedProduct.apiType === 'consumer') {
        filtered = filtered.filter((r: any) =>
          this.getConsumerFullName(r).toLowerCase().includes(q) ||
          (r.email || '').toLowerCase().includes(q) ||
          (r.phone || '').toLowerCase().includes(q)
        );
      } else {
        filtered = filtered.filter((r: any) =>
          (r.name            || '').toLowerCase().includes(q) ||
          (r.contact_person  || '').toLowerCase().includes(q) ||
          (r.email           || '').toLowerCase().includes(q) ||
          (r.contact_phone   || '').toLowerCase().includes(q) ||
          (r.status          || '').toLowerCase().includes(q)
        );
      }
    }

    this.filteredData = filtered;
    this.pageIndex = 0;

    if (this.sortColumn && this.sortDirection) {
      this.applySort();
    } else {
      this.dataSource = [...this.filteredData];
    }
  }

  applySort(): void {
    if (!this.sortColumn || this.sortDirection === '') {
      this.dataSource = [...this.filteredData];
      return;
    }

    const data = [...this.filteredData];
    const dir  = this.sortDirection === 'asc' ? 1 : -1;
    const col  = this.sortColumn;

    data.sort((a: any, b: any) => {
      let valA: any;
      let valB: any;

      if (col === 'name' && this.selectedProduct?.apiType === 'consumer') {
        valA = this.getConsumerFullName(a).toLowerCase();
        valB = this.getConsumerFullName(b).toLowerCase();
      } else if (col === 'account_activation_date' || col === 'last_login' || col === 'joined') {
        const tA = a[col] ? new Date(a[col]).getTime() : 0;
        const tB = b[col] ? new Date(b[col]).getTime() : 0;
        return (tA - tB) * dir;
      } else {
        valA = (a[col] || '').toString().toLowerCase();
        valB = (b[col] || '').toString().toLowerCase();
      }

      if (valA < valB) return -1 * dir;
      if (valA > valB) return  1 * dir;
      return 0;
    });

    this.dataSource = data;
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      if (this.sortDirection === 'asc')       { this.sortDirection = 'desc'; }
      else if (this.sortDirection === 'desc') { this.sortDirection = ''; this.sortColumn = ''; }
      else                                    { this.sortDirection = 'asc'; }
    } else {
      this.sortColumn    = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column || this.sortDirection === '') {
      return 'bi-arrow-down-up sort-icon-neutral';
    }
    return this.sortDirection === 'asc'
      ? 'bi-arrow-up sort-icon-active'
      : 'bi-arrow-down sort-icon-active';
  }

  // ═══════════════════════════════════════════════════════════
  //  SEARCH
  // ═══════════════════════════════════════════════════════════

  onSearch(): void { this.applyFilter(); }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter();
  }

  // ═══════════════════════════════════════════════════════════
  //  PAGINATION
  // ═══════════════════════════════════════════════════════════

  getPage(): any[] {
    const start = this.pageIndex * this.pageSize;
    return this.dataSource.slice(start, start + this.pageSize);
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.dataSource.length / this.pageSize));
  }

  getPageNumbers(): number[] {
    const total   = this.getTotalPages();
    const current = this.pageIndex + 1;
    const maxVis  = 5;
    let start = Math.max(1, current - Math.floor(maxVis / 2));
    let end   = Math.min(total, start + maxVis - 1);
    if (end - start < maxVis - 1) start = Math.max(1, end - maxVis + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getEndIndex(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.dataSource.length);
  }

  getPaginationRange(): string {
    if (this.dataSource.length === 0) return '0-0 of 0 items';
    return `${this.pageIndex * this.pageSize + 1}-${this.getEndIndex()} of ${this.dataSource.length} items`;
  }

  goToFirstPage(): void { if (this.pageIndex !== 0) this.pageIndex = 0; }
  goToPrevPage(): void  { if (this.pageIndex > 0) this.pageIndex--; }
  goToNextPage(): void  { if (this.pageIndex < this.getTotalPages() - 1) this.pageIndex++; }
  goToLastPage(): void  { this.pageIndex = this.getTotalPages() - 1; }
  goToPage(index: number): void {
    if (index >= 0 && index < this.getTotalPages()) this.pageIndex = index;
  }

  // ═══════════════════════════════════════════════════════════
  //  TEMPLATE HELPERS
  // ═══════════════════════════════════════════════════════════

  isConsumerMode(): boolean {
    return this.selectedProduct?.apiType === 'consumer';
  }

  isProfessionalMode(): boolean {
    return this.selectedProduct?.apiType === 'professional';
  }

  /** Consumer: combine first + last name, or use the single name field */
  getConsumerFullName(r: any): string {
    // Try new API format first (single 'name' field)
    if (r.name) {
      return (r.name || '').trim() || '-';
    }
    // Fall back to legacy format (first_name + last_name)
    const first = (r.first_name || '').trim();
    const last  = (r.last_name  || '').trim();
    if (!first && !last) return '-';
    return [first, last].filter(Boolean).join(' ');
  }

  /** Initials — works for both consumer (first/last or single name) and professional (firm name) */
  getInitials(r: any): string {
    if (this.isConsumerMode()) {
      // Try new API format first (single 'name' field)
      if (r.name) {
        const name = (r.name || '').trim();
        if (!name) return '?';
        const parts = name.split(' ');
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
      }
      // Fall back to legacy format (first_name + last_name)
      const first = (r.first_name || '').trim();
      const last  = (r.last_name  || '').trim();
      if (!first && !last) return '?';
      if (first && last) return (first.charAt(0) + last.charAt(0)).toUpperCase();
      return (first || last).charAt(0).toUpperCase();
    } else {
      const name = (r.name || '').trim();
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
  }

  formatDate(date: string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit'
    });
  }

  formatDateTime(date: string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    const dateStr = d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit'
    });
    const timeStr = d.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
    });
    return `${dateStr} | ${timeStr}`;
  }

  getStatusClass(status: string): string {
    const map: { [key: string]: string } = {
      'active':  'status-active',
      'pending': 'status-pending',
      'invited': 'status-invited'
    };
    return map[(status || '').toLowerCase()] || 'status-default';
  }

  getLastLoginClass(lastlogin: string | null): string {
    if (!lastlogin) return 'login-never';
    const days = (Date.now() - new Date(lastlogin).getTime()) / (1000 * 60 * 60 * 24);
    if (days <= 30)  return 'login-recent';
    if (days <= 180) return 'login-moderate';
    return 'login-old';
  }
}